
import { useState } from 'react';

interface RoleManagementDropdownProps {
  memberId: string;
  memberName: string;
  currentRole: 'admin' | 'moderator' | 'member';
  currentUserRole: 'owner' | 'admin' | 'moderator' | 'member';
  onRoleChange: (userId: string, newRole: string) => void;
  isLoading?: boolean;
}

const RoleManagementDropdown = ({
  memberId,
  memberName,
  currentRole,
  currentUserRole,
  onRoleChange,
  isLoading = false,
}: RoleManagementDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ role: string; label: string } | null>(null);

  const isOwner = currentUserRole === 'owner';
  const isAdmin = currentUserRole === 'admin';

  const roleOptions = [
    {
      role: 'admin',
      label: 'Admin',
      description: 'Can manage members, posts, and settings',
      icon: 'ri-shield-star-fill',
      color: 'text-rose-500',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      canAssign: isOwner,
    },
    {
      role: 'moderator',
      label: 'Moderator',
      description: 'Can moderate posts and mute members',
      icon: 'ri-shield-check-fill',
      color: 'text-teal-500',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      canAssign: isOwner || isAdmin,
    },
    {
      role: 'member',
      label: 'Member',
      description: 'Regular group member',
      icon: 'ri-user-fill',
      color: 'text-gray-400',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      canAssign: isOwner || (isAdmin && currentRole === 'moderator'),
    },
  ];

  const availableOptions = roleOptions.filter(
    (opt) => opt.role !== currentRole && opt.canAssign
  );

  const handleSelectRole = (role: string, label: string) => {
    setConfirmAction({ role, label });
  };

  const handleConfirm = () => {
    if (confirmAction) {
      onRoleChange(memberId, confirmAction.role);
      setConfirmAction(null);
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setConfirmAction(null);
  };

  if (availableOptions.length === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={() => { setIsOpen(!isOpen); setConfirmAction(null); }}
        disabled={isLoading}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
      >
        <i className="ri-user-settings-line text-base"></i>
        <span>Change Role</span>
        <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-sm`}></i>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setIsOpen(false); setConfirmAction(null); }} />
          <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Change Role</p>
              <p className="text-sm text-gray-700 mt-0.5 truncate">{memberName}</p>
            </div>

            {/* Confirmation View */}
            {confirmAction ? (
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <i className="ri-error-warning-line text-amber-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Confirm Role Change</p>
                    <p className="text-xs text-gray-500">
                      Change <strong>{memberName}</strong> to <strong>{confirmAction.label}</strong>?
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCancel}
                    className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={isLoading}
                    className="flex-1 px-3 py-2 text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {isLoading ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      'Confirm'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* Role Options */
              <div className="p-2">
                {availableOptions.map((option) => (
                  <button
                    key={option.role}
                    onClick={() => handleSelectRole(option.role, option.label)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg hover:${option.bgColor} transition-colors cursor-pointer text-left`}
                  >
                    <div className={`w-8 h-8 rounded-full ${option.bgColor} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <i className={`${option.icon} ${option.color}`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{option.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                    </div>
                    {currentRole === option.role ? (
                      <span className="text-xs text-teal-600 font-medium bg-teal-50 px-2 py-0.5 rounded-full whitespace-nowrap">Current</span>
                    ) : (
                      <i className="ri-arrow-right-s-line text-gray-300 mt-1"></i>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RoleManagementDropdown;
